import {
  addDays,
  BusinessCalendar,
  CustomHolidayProvider,
  PortugueseNationalHolidays,
  type DateOnly,
} from '@/domain/dates'
import type { CustomDay } from './types'

/**
 * Working-day calendar of an organization: weekends + Portuguese national holidays
 * (computed, incl. Good Friday and Corpus Christi) + the organization's own holidays.
 * Bridges ("pontes") are NOT holidays: they are marked in the UI but count as vacation days.
 */
export function buildCalendar(customDays: CustomDay[] = []): BusinessCalendar {
  const custom = customDays
    .filter((d) => d.kind === 'holiday')
    .map((d) => ({ date: d.date, name: d.name, scope: 'custom' as const }))
  return new BusinessCalendar([new PortugueseNationalHolidays(), new CustomHolidayProvider(custom)])
}

/** Working days in [start, end], inclusive. */
export function workingDaysBetween(cal: BusinessCalendar, start: DateOnly, end: DateOnly): number {
  if (end < start) return 0
  let n = 0
  for (let d = start; d <= end; d = addDays(d, 1)) if (cal.isBusinessDay(d)) n += 1
  return n
}

/** Working days of [start, end] that fall inside `year`. */
export function workingDaysInYear(cal: BusinessCalendar, start: DateOnly, end: DateOnly, year: number): number {
  const from = start < `${year}-01-01` ? (`${year}-01-01` as DateOnly) : start
  const to = end > `${year}-12-31` ? (`${year}-12-31` as DateOnly) : end
  return workingDaysBetween(cal, from, to)
}

/**
 * The full rest window around a period: it grows over the weekends and holidays
 * that touch it. "Mon–Thu before a Friday holiday" = 4 working days, 5 days off…
 * plus the weekend = 7 consecutive days of rest.
 */
export function restWindow(cal: BusinessCalendar, start: DateOnly, end: DateOnly): { from: DateOnly; to: DateOnly; days: number } {
  let from = start
  let to = end
  while (!cal.isBusinessDay(addDays(from, -1))) from = addDays(from, -1)
  while (!cal.isBusinessDay(addDays(to, 1))) to = addDays(to, 1)
  const days = Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000) + 1
  return { from, to, days }
}
